import {
  Box,
  Flex,
  Price,
  SystemProps,
  Text, LegacyText,
  TransparentButton,
} from "flicket-ui";
import { useMemo } from "react";
import styled from "styled-components";

import { Icon } from "~components/common/Icon";
import { SortTypes } from "~components/reservation/constants";
import * as actions from "~features/seated-reservation/store/actions/index";
import { sortAvailableItems } from "../../helpers/zoneFilters";
import { useSeekaConverge } from "@seeka-labs/converge-react";

type StyledListItemProps = SystemProps & {
  isActive: boolean;
  isDisabled: boolean;
};

const StyledListItem = styled(Flex)<StyledListItemProps>`
  padding: 10px 12px 8px 12px;

  justify-content: space-between;

  background-color: ${(p) => p.theme.colors.white};

  border: 1px solid ${(p) => p.theme.colors.N200};
  border-left: 6px solid ${(p) => p.color};
  border-radius: ${(p) => p.theme.radii.sm};

  transition: border-color 0.2s, box-shadow 0.2s;

  cursor: pointer;

  ${(p) =>
    p.isActive &&
    `
    border-color: ${p.color};
    box-shadow: 0px 9px 39px rgba(0, 0, 0, 0.08),
      0px 2.01027px 8.71116px rgba(0, 0, 0, 0.0575),
      0px 0.598509px 2.59354px rgba(0, 0, 0, 0.0325);
  `}

  &:hover {
    /** @todo add these styles for selected item as well */
    border-color: ${(p) => p.color};
    box-shadow: 0px 9px 39px rgba(0, 0, 0, 0.08),
      0px 2.01027px 8.71116px rgba(0, 0, 0, 0.0575),
      0px 0.598509px 2.59354px rgba(0, 0, 0, 0.0325);
  }

  ${(p) => {
    const disabledGrey = p.theme.colors.N200;
    return (
      p.isDisabled &&
      `
      border-color: ${disabledGrey};
      border-left: 6px solid ${disabledGrey};
      cursor: auto;

      &:hover {
        border-color: ${disabledGrey};
        box-shadow: none;
      }
  `
    );
  }}
`;

const ListItem = ({ zone, handleClick, isActive, pricing }) => {
  let hasTicketsAvailable =
    zone?.ticketTypes?.length > 0 || zone?.sectionTicketTypes?.length > 0;

  if (zone.isSoldOut) {
    hasTicketsAvailable = false;
  }

  const priceMin = zone?.priceRange?.min ?? pricing?.min;
  const priceMax = zone?.priceRange?.max ?? pricing?.max;

  const showMinPrice = priceMin > 0;
  const showMaxPrice = priceMax != priceMin || !showMinPrice;

  return (
    <StyledListItem
      mb={{ _: "6/4", md: 2 }}
      onClick={() => hasTicketsAvailable && handleClick(zone)}
      isActive={isActive}
      isDisabled={!hasTicketsAvailable}
      key={zone.id}
      color={zone?.categories?.[0]?.color}
    >
      <Flex>
        <Flex flexDir="column">
          <LegacyText
            fontSize={3}
            lineHeight="medium"
            fontWeight="extraBold"
            color={hasTicketsAvailable ? "N800" : "N300"}
          >
            {zone?.name}
          </LegacyText>

          {!hasTicketsAvailable && (
            <LegacyText fontSize={2} lineHeight="medium" color="N300" mb="1/4">
              {zone.isSoldOut ? "Sold out" : "No tickets available"}
            </LegacyText>
          )}
          {hasTicketsAvailable && (
            <LegacyText fontSize={2} lineHeight="medium" color="N600" mb="1/4">
              {showMinPrice && <Price price={priceMin} />}
              {showMinPrice && showMaxPrice && " - "}
              {showMaxPrice && <Price price={priceMax} />}
            </LegacyText>
          )}
        </Flex>
      </Flex>
      <Flex>
        {hasTicketsAvailable && zone?.tags?.includes("f") && (
          <Flex
            alignSelf="center"
            alignItems="center"
            borderRight="1px solid"
            borderColor="N200"
            pr="6/4"
            mr={1}
          >
            <Icon fontSize={7} icon="alcohol-free" />
          </Flex>
        )}
        {hasTicketsAvailable && (
          <TransparentButton>
            <Icon fontSize={6} icon="chevron-right" color="P300" />
          </TransparentButton>
        )}
      </Flex>
    </StyledListItem>
  );
};

export const PlaceholderListItem = ({ id }) => (
  <StyledListItem
    mb={{ _: "6/4", md: 2 }}
    key={id}
    isActive={false}
    isDisabled={false}
  >
    <Flex w="100%" flexDir="column">
      <LegacyText
        w="40%"
        mt={"1px" as any}
        mb={"3px" as any}
        height="16px"
        bg="N200"
        opacity="0.7"
        borderRadius="sm"
      />
      <LegacyText
        w="70%"
        mt={"1px" as any}
        mb={"3px" as any}
        height="18px"
        bg="N200"
        opacity="0.4"
        borderRadius="sm"
      />
    </Flex>
    <TransparentButton>
      <Icon fontSize={6} icon="chevron-right" color="N200" />
    </TransparentButton>
  </StyledListItem>
);

type ListProps = {
  currentSort: SortTypes;
  zones: any;
  index: "zone" | "section";
  selected: any;
  pricing: any;
  isPlaceholder?: boolean;
};

export const List = ({
  currentSort,
  zones,
  index,
  selected,
  pricing,
  isPlaceholder = false,
}: ListProps) => {
  const converge = useSeekaConverge();

  const handleClick = async (item: any) => {
    if (index === "zone") {
      await actions.setCurrentZone({ zone: item }, converge);
    } else if (index === "section") {
      await actions.setCurrentSection({ section: item }, converge);
    }
  };
  const data = Array.isArray(zones) ? zones : zones?.sections;
  const sortedData = useMemo(() => sortAvailableItems(data, currentSort), [
    data,
    currentSort,
  ]);

  if (isPlaceholder) {
    return (
      <Box>
        {[...Object(Array(4)).keys()].map((i) => (
          <PlaceholderListItem id={`placeholder-${i}`} key={i} />
        ))}
      </Box>
    );
  }
  return (
    <Box>
      {sortedData?.map((item) => {
        return (
          <ListItem
            key={item.name}
            handleClick={handleClick}
            zone={item}
            pricing={pricing}
            isActive={item?.name === selected?.name}
          />
        );
      })}
    </Box>
  );
};
